import { create } from 'zustand';
import type { CopilotPendingConfirmation } from '@/lib/api';

const STORAGE_KEY = 'oneup.alice.pendingApprovals.v1';
const MAX_PENDING = 20;

export interface AlicePendingApprovalItem {
  id: string;
  pending: CopilotPendingConfirmation;
  createdAt: string;
  updatedAt: string;
  feedback?: string;
  error?: string;
}

interface AliceApprovalState {
  items: AlicePendingApprovalItem[];
  addApproval: (pending: CopilotPendingConfirmation) => void;
  removeApproval: (id: string) => void;
  setApprovalError: (id: string, error: string | null) => void;
  setApprovalFeedback: (id: string, feedback: string) => void;
}

export const useAliceApprovalStore = create<AliceApprovalState>((set, get) => ({
  items: readItems(),
  addApproval: (pending) => {
    const now = new Date().toISOString();
    const existing = get().items.find((item) => item.id === pending.toolCallId);
    const nextItem: AlicePendingApprovalItem = {
      id: pending.toolCallId,
      pending,
      createdAt: existing?.createdAt ?? now,
      updatedAt: now,
      ...(existing?.feedback ? { feedback: existing.feedback } : {}),
      ...(existing?.error ? { error: existing.error } : {}),
    };
    const next = [
      nextItem,
      ...get().items.filter((item) => item.id !== pending.toolCallId),
    ].slice(0, MAX_PENDING);
    writeItems(next);
    set({ items: next });
  },
  removeApproval: (id) => {
    const next = get().items.filter((item) => item.id !== id);
    writeItems(next);
    set({ items: next });
  },
  setApprovalError: (id, error) => {
    const next = get().items.map((item) => {
      if (item.id !== id) return item;
      const rest: AlicePendingApprovalItem = { ...item, updatedAt: new Date().toISOString() };
      delete rest.error;
      return {
        ...rest,
        ...(error ? { error } : {}),
      };
    });
    writeItems(next);
    set({ items: next });
  },
  setApprovalFeedback: (id, feedback) => {
    const next = get().items.map((item) =>
      item.id === id
        ? { ...item, feedback, updatedAt: new Date().toISOString() }
        : item,
    );
    writeItems(next);
    set({ items: next });
  },
}));

function readItems(): AlicePendingApprovalItem[] {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw) as { items?: unknown };
    if (!Array.isArray(parsed.items)) return [];
    return parsed.items.filter(isApprovalItem).slice(0, MAX_PENDING);
  } catch {
    return [];
  }
}

function writeItems(items: AlicePendingApprovalItem[]): void {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify({ version: 1, items }));
  } catch {
    /* ignore private mode / disabled storage */
  }
}

function isApprovalItem(value: unknown): value is AlicePendingApprovalItem {
  if (!value || typeof value !== 'object') return false;
  const item = value as Partial<AlicePendingApprovalItem>;
  return (
    typeof item.id === 'string' &&
    typeof item.createdAt === 'string' &&
    typeof item.updatedAt === 'string' &&
    Boolean(item.pending) &&
    typeof item.pending?.toolCallId === 'string' &&
    typeof item.pending?.toolName === 'string' &&
    typeof item.pending?.summary === 'string' &&
    Boolean(item.pending?.args) &&
    typeof item.pending?.args === 'object'
  );
}
